import React, { useRef, useState } from 'react';
import { Form, Button, Collapse, Row, Col, FormInstance, Input, Tooltip } from 'antd';
import { SearchOutlined, StopOutlined, LeftOutlined, FilterFilled, UpOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { enmControlType, IProperty } from './interfaces';
import { ColumnFilter } from './columnfilter';
import { AndFilter, ComparisonFilter, IFilter, enmConditionalOperators } from '../infrastructure/filtering';
import { IBaseComponent } from '../infrastructure/baseComponent';
import { useEffect } from 'react';
type LayoutType = Parameters<typeof Form>[0]['layout'];
const { Panel } = Collapse;
const { Search } = Input;

export interface ISearchFormProps {
    onSubmit: (values: any, filter?: IFilter) => void;
    onSearch: (value: any) => void;
    onReset: () => void;
    render?: () => React.ReactNode[];
    renderExtraButtons?: () => React.ReactNode;
    columnsPerRow?: number;
    loading: boolean;
    properties: IProperty[];
    onFilterChanged?: (property: IProperty, value?: { operator: enmConditionalOperators; value?: any; }, form?: FormInstance<any>) => void;
    style?: React.CSSProperties;
    defaultValues?: () => {
        [key: string]: { operator: enmConditionalOperators; value?: any; };
    };
    parent: IBaseComponent;
    preparelookupProps?: (propName: string, lookupProps: any, form?: FormInstance<any>) => any;
    onLookupOpen?: (propName: string) => void;
    onLookupClose?: (propName: string, selectetEntity?: any) => void;
}

interface ISearchFormStates {
    searchFilters: React.ReactNode[];
    isOpen?: boolean;
}

const SearchForm = (props: ISearchFormProps) => {
    const [form] = Form.useForm();
    const [formLayout] = useState<LayoutType>('horizontal');

    const [state, setState] = useState<ISearchFormStates>({ searchFilters: [] });
    const { searchFilters } = state;

    const values = useRef<{ [key: string]: { operator: enmConditionalOperators; value?: any; } }>({});

    useEffect(() => {
        setState({ ...state, searchFilters: [] });
    }, [props.render]);

    if (!searchFilters.length) {

        if (props.render)
            searchFilters.push(...props.render());

        const defaultValues = props.defaultValues && props.defaultValues();
        for (let key in defaultValues) {
            values.current[key] = defaultValues[key];
        }

        props.properties.forEach(c => {
            if (c.filterable && c.controlType && c.dataIndex) {
                searchFilters.push(
                    <Form.Item name={c.dataIndex} key={c.dataIndex} label={c.title} >
                        <ColumnFilter
                            initialValue={values.current && values.current[c.dataIndex]}
                            parent={props.parent}
                            property={c}
                            onChange={currentFilter => {
                                let value = c.lookup?.lookupProperty && currentFilter.value ? currentFilter.value["Id"] : currentFilter.value;
                                values.current[c.dataIndex!] = { operator: currentFilter.operator, value };

                                if (values.current[c.dataIndex!].value === '')
                                    delete values.current[c.dataIndex!];

                                if (c.controlType === enmControlType.combobox && !currentFilter.value && currentFilter.value !== 0 && currentFilter.value !== "") {
                                    if (props.onFilterChanged)
                                        props.onFilterChanged(c, undefined, form);
                                    return;
                                }

                                if (c.controlType === enmControlType.checkbox && currentFilter.value === undefined) {
                                    if (props.onFilterChanged)
                                        props.onFilterChanged(c, undefined, form);
                                    return;
                                }

                                if (props.onFilterChanged)
                                    props.onFilterChanged(c, currentFilter, form);
                            }}
                            preparelookupProps={lookupProps => {
                                if (props.preparelookupProps)
                                    return props.preparelookupProps(c.dataIndex!, lookupProps, form);
                                return lookupProps;
                            }}
                            onLookupOpen={() => props.onLookupOpen && props.onLookupOpen(c.dataIndex!)}
                            onLookupClose={(selectedEntity) => props.onLookupClose && props.onLookupClose(c.dataIndex!, selectedEntity)}

                        />
                    </Form.Item>
                );
            }
        });
    }

    let colsPerRow = props.columnsPerRow ?? 1;
    let searchAreaRows: React.ReactNode[] = []
    for (let i = 0; i < searchFilters.length / colsPerRow; i++) {
        searchAreaRows.push(
            <Row key={i} style={{ marginTop: 0 }}>
                {
                    searchFilters.slice(i * colsPerRow, (i + 1) * colsPerRow).map((a, j) => {
                        return (<Col key={j} lg={24 / colsPerRow - 1} md={23} sm={23} xs={23} offset={1}>{a}</Col>);
                    })
                } </Row>
        );
    }

    let filterTitle = "جستجوی پیشرفته";

    const style = !!props.style ? { ...props.style } : { boxShadow: 'rgb(0 0 0 / 12%) 0px 0px 5px 2px', margin: '10px 0px' };
    const formItemLayout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 16 },
    };

    return (
        <div style={style}>
            <Form
                {...formItemLayout}
                layout={formLayout}
                form={form}
                initialValues={{ layout: formLayout }}
                onFinish={() => {
                    let allFilters: IFilter[] = [];
                    for (let key in values.current) {
                        let filter = values.current[key];
                        if (filter && filter.value !== undefined)
                            allFilters.push(new ComparisonFilter(key, filter.operator, filter.value))
                    }
                    let filter: IFilter | undefined;
                    if (allFilters.length)
                        filter = new AndFilter(allFilters);
                    props.onSubmit(values.current, filter);
                }}
                onReset={() => {
                    for (let key in values.current) {
                        delete values.current[key];
                    }
                    const defaultValues = props.defaultValues && props.defaultValues();
                    for (let key in defaultValues) {
                        values.current[key] = defaultValues[key];
                    }
                    props.onReset();
                }}>

                <Collapse activeKey={state.isOpen === true ? "1" : undefined} style={{ background: "rgb(137 167 235 / 20%)" }}>
                    <Panel key="1"
                        showArrow={false}
                        collapsible="header"
                        header={
                            <Row>
                                <Col span={2} lg={2} md={2} sm={2} xs={2}>
                                    <span style={{ fontSize: 10, verticalAlign: 'sub', marginRight: 10 }} onClick={() => setState({ ...state, isOpen: !state.isOpen })}>{state.isOpen === true ? <UpOutlined /> : <LeftOutlined />}</span>
                                </Col>
                                <Col span={4} lg={4} md={4} sm={4} xs={4}>
                                    <span style={{ verticalAlign: 'sub' }} onClick={() => setState({ ...state, isOpen: !state.isOpen })}>جستجو</span>
                                </Col>
                                <Col span={12} lg={12} md={12} sm={12} xs={12}>
                                    <Search placeholder="جستجو" allowClear={true} onSearch={props.onSearch} />
                                </Col>
                                <Col span={2} lg={2} md={2} sm={2} xs={2} >
                                    <Tooltip title={'Refresh'}>
                                        <Button style={{ marginRight: 1, width: 30, padding: 0, verticalAlign: 'middle' }} onClick={form.submit} icon={!props.loading ? <ReloadOutlined /> : <LoadingOutlined />}>

                                        </Button>
                                    </Tooltip>
                                </Col>
                                <Col span={2} lg={2} md={2} sm={2} xs={2} >
                                    <Tooltip title={filterTitle}>
                                        <Button style={{ marginRight: 3, padding: 0, width: 30, verticalAlign: 'middle' }} onClick={() => setState({ ...state, isOpen: !state.isOpen })} icon={<FilterFilled />} />
                                    </Tooltip>
                                </Col>
                            </Row>
                        }
                        style={{ border: '0px', padding: 0, margin: 0 }}
                    >

                        {<>{searchAreaRows}</>}

                        <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ marginTop: 0 }}>
                            {props.renderExtraButtons?.()}
                            <Button key={"submit"} type="primary" htmlType="submit" loading={props.loading} icon={<SearchOutlined />} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10, marginLeft: 10, padding: 10 }}>
                                جستجو
                            </Button>
                            <Button key={"reset"} type="ghost" htmlType="reset" icon={<StopOutlined />} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10, marginLeft: 10, background: '#fff' }}>
                                ریست
                            </Button>
                        </Form.Item>
                    </Panel>
                </Collapse>
            </Form>
        </div>
    );
};

export default SearchForm;